import { useState, useRef } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import ScrollableContainer from "../../ScrollableContainer";
import DistanceFilter, { DistanceFilterRef } from "./DistanceFilter";
import DurationFilter, { DurationFilterRef } from "./DurationFilter";
import FatigueFilter from "./FatigueFilter";

interface FiltersDropdownProps {
  onFilterChange?: (filters: FilterValues) => void;
}

export interface FilterValues {
  minDistance?: number;
  maxDistance?: number;
  minDuration?: number;
  maxDuration?: number;
  startDate?: Date | null;
  endDate?: Date | null;
  // Add other filter values as needed
}

export default function FiltersDropdown({ onFilterChange }: FiltersDropdownProps) {
  // Local state for filter values (not applied until Apply button is clicked)
  const [pendingFilters, setPendingFilters] = useState<FilterValues>({});
  
  // Track if filters have been modified since last apply
  const [filtersModified, setFiltersModified] = useState(false);
  
  // Refs to filter components for resetting
  const distanceFilterRef = useRef<DistanceFilterRef>(null);
  const durationFilterRef = useRef<DurationFilterRef>(null);

  const handleDistanceFilterChange = (minDistance: number, maxDistance: number) => {
    setPendingFilters(prev => ({
      ...prev,
      minDistance,
      maxDistance
    }));
    setFiltersModified(true);
  };

  const handleDurationFilterChange = (minDuration: number, maxDuration: number) => {
    setPendingFilters(prev => ({
      ...prev,
      minDuration,
      maxDuration
    }));
    setFiltersModified(true);
  };

  const handleApplyFilters = () => {
    if (onFilterChange) {
      onFilterChange(pendingFilters);
    }
    setFiltersModified(false);
  };

  const handleClearFilters = () => {
    // Reset the filter components UI
    if (distanceFilterRef.current) {
      distanceFilterRef.current.reset();
    }
    
    if (durationFilterRef.current) {
      durationFilterRef.current.reset();
    }
    
    // Clear the pending filters state
    setPendingFilters({});
    setFiltersModified(true);
    
    // Notify parent component
    if (onFilterChange) {
      onFilterChange({});
    }
  };

  return (
    <Container className="filter-drop-container py-2 px-3">
      <Row>
        <Col><span className="filter-drop-heading">Filters</span></Col>
        <Col className="d-flex justify-content-end">
          <a onClick={handleClearFilters} className="wind-link" style={{textDecoration: "none", cursor: "pointer"}}>
            Clear All
          </a>
        </Col>
      </Row>
      <Row className="divider my-3 mx-0" />
      
      {/* Distance Filter */}
      <Row className="py-2">
        <Col>
          <span className="filter-drop-subheading">Distance (km)</span>
        </Col>
      </Row>
      <Row className="py-2">
        <Col>
          <DistanceFilter 
            ref={distanceFilterRef}
            onFilterChange={handleDistanceFilterChange} 
          />
        </Col>
      </Row>
      
      <Row className="divider my-3 mx-0" />
      
      {/* Duration Filter */}
      <Row className="py-2">
        <Col>
          <span className="filter-drop-subheading">Duration (minutes)</span>
        </Col>
      </Row>
      <Row className="py-2">
        <Col>
          <DurationFilter 
            ref={durationFilterRef}
            onFilterChange={handleDurationFilterChange} 
          />
        </Col>
      </Row>
      
      <Row className="divider my-3 mx-0" />
      
      {/* Fatigue Filter */}
      {/* <Row className="py-2">
        <Col>
          <span className="filter-drop-subheading">Amount of fatigue</span>
        </Col>
      </Row>
      <Row className="py-2">
        <Col>
          <ScrollableContainer content={<FatigueFilter />} maxHeight="20vh"/>
        </Col>
      </Row>
      
      <Row className="divider my-3 mx-0" /> */}
      
      {/* Apply Filters Button */}
      <Row className="py-2">
        <Col className="d-flex justify-content-center">
          <Button 
            variant="primary" 
            onClick={handleApplyFilters}
            disabled={!filtersModified}
            className="px-4"
          >
            Apply Filters
          </Button>
        </Col>
      </Row>
    </Container>
  );
}
